<script setup lang="ts">
import { CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { tv, type VariantProps } from 'tailwind-variants/lite'
import { computed, type Component } from 'vue'
import type { ToastAction } from '../../composables'
import { LButton } from '../button'
import { LIcon } from '../icon'
import { LIconButton } from '../icon-button'

const style = tv({
  base: 'lui-toast',
  slots: {
    icon: 'lui-toast__icon',
    text: 'lui-toast__text',
    action: 'lui-toast__action',
    closeButton: 'lui-toast__close-button',
  },
  variants: {
    variant: {
      success: 'lui-toast--variant-success',
      error: 'lui-toast--variant-error',
    },
  },
})

export interface ToastProps {
  text: string
  variant?: VariantProps<typeof style>['variant']
  action?: ToastAction
  closable?: boolean
}

const props = withDefaults(defineProps<ToastProps>(), {
  variant: 'success',
  closable: false,
})

const emit = defineEmits<{
  (e: 'click:action'): void
  (e: 'close'): void
}>()

const classes = computed(() =>
  style({
    variant: props.variant,
  }),
)

const iconComponent = computed<Component>(() => {
  return props.variant === 'error' ? ExclamationTriangleIcon : CheckCircleIcon
})

function handleActionClick() {
  props.action?.onClick()
  emit('click:action')
}

function handleClose() {
  emit('close')
}
</script>

<template>
  <div :class="classes.base()" :role="props.variant === 'error' ? 'alert' : 'status'">
    <LIcon :is="iconComponent" size="md" :class="classes.icon()" />
    <span :class="classes.text()">{{ text }}</span>
    <LButton
      v-if="action"
      variant="link"
      tone="invert"
      size="sm"
      :class="classes.action()"
      :aria-label="`${action.label}: ${text}`"
      @click="handleActionClick"
    >
      {{ action.label }}
    </LButton>
    <LIconButton
      v-if="closable"
      :class="classes.closeButton()"
      :icon="XMarkIcon"
      label="Close notification"
      variant="ghost"
      tone="invert"
      size="sm"
      @click="handleClose"
    />
  </div>
</template>

<style>
@reference "../../css/tailwind.css";

@layer components {
  .lui-toast {
    @apply flex items-center text-balance wrap-anywhere;
  }

  .lui-toast__text {
    @apply flex-1;
  }

  .lui-toast__action {
    @apply px-0;
  }
}
</style>
